import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangePretty, formatFYLabel } from "@/lib/useReportRange";
import { exportExcel, exportFinancialPDF } from "@/lib/exporters";
import { Boxes, ChevronLeft } from "lucide-react";
import { devError } from "@/lib/logger";

/**
 * Item Ledger — every transaction touching a single stock item, date-wise.
 *
 * Layout:
 *   • Item picker (dropdown if no :iid in URL, fixed header when :iid is set)
 *   • Date-range picker with explicit Go button (controls applied range)
 *   • Opening balance row
 *   • Chronological table: Date / Voucher No / Type / Party / Inward (Qty + Value) /
 *     Outward (Qty + Value) / Rate / Running Balance (Qty + Value)
 *   • Closing balance row + period totals
 *   • Excel + PDF + Print export
 *
 * Backend: GET /api/companies/{cid}/reports/item-ledger/{iid}?start=&end=
 */
function Inner() {
  const { iid: iidParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;

  const [items, setItems] = useState([]);
  const [iid, setIid] = useState(iidParam || "");
  const [data, setData] = useState(null);

  // Load items once
  useEffect(() => {
    if (!activeId) return;
    api
      .get(`/companies/${activeId}/items`)
      .then((r) => {
        setItems(r.data);
        // If no iid in URL but items exist, preselect the first one.
        if (!iidParam && r.data.length > 0) setIid(r.data[0].id);
      })
      .catch((err) => devError("items load", err));
  }, [activeId, iidParam]);

  // Sync iid when the URL param changes
  useEffect(() => { if (iidParam) setIid(iidParam); }, [iidParam]);

  // Fetch ledger when iid or applied range changes.
  useEffect(() => {
    if (!activeId || !iid) return;
    api
      .get(`/companies/${activeId}/reports/item-ledger/${iid}`, { params })
      .then((r) => setData(r.data))
      .catch((err) => devError("item ledger load", err));
  }, [activeId, iid, applied.start, applied.end, params]);

  const item = useMemo(() => items.find((x) => x.id === iid), [items, iid]);

  const onCSV = () => {
    if (!data) return;
    const head = [
      "Date", "Voucher No", "Voucher Type", "Party / Reference",
      "Inward Qty", "Inward Value (Rs)", "Outward Qty", "Outward Value (Rs)",
      "Rate (Rs)", "Disc %", "Disc (Rs)", "GST %", "GST (Rs)",
      "Running Qty", "Running Value (Rs)",
    ];
    const meta = [
      [`Item: ${item?.name || iid}`], [`Unit: ${data.item.unit}    HSN: ${data.item.hsn || "—"}    GST: ${data.item.gst_rate}%`],
      [`Period: ${formatRangePretty(applied)}`], [],
    ];
    const opening = [
      "Opening Balance", "", "", "",
      "", "", "", "",
      "", "", "", "", "",
      data.opening.qty, data.opening.value,
    ];
    const body = data.rows.map((r) => [
      r.date, r.voucher_no, r.voucher_type, r.party_ledger_name || r.reference || "",
      r.inward_qty || "", r.inward_value || "",
      r.outward_qty || "", r.outward_value || "",
      r.rate, r.discount_pct || 0, r.discount_amount || 0,
      r.gst_pct || 0, r.gst_amount || 0,
      r.running_qty, r.running_value,
    ]);
    const closing = [
      "Closing Balance", "", "", "",
      "", "", "", "",
      "", "", "", "", "",
      data.closing.qty, data.closing.value,
    ];
    exportExcel(`item-ledger-${item?.name || iid}-${active?.name || "report"}`, [
      { name: "Item Ledger", rows: [...meta, head, opening, ...body, closing] },
    ]);
  };

  const onPDF = () => {
    if (!data) return;
    const head = ["Date", "Voucher", "Type", "Party", "Inward", "Outward", "Rate (Rs.)", "Balance"];
    const rows = [];
    rows.push({ cells: ["Opening Balance", "", "", "", "", "", "",
      `${data.opening.qty} ${data.item.unit}  (Rs. ${formatINR(data.opening.value)})`], type: "group" });
    data.rows.forEach((r) => {
      rows.push([
        r.date, r.voucher_no, r.voucher_type,
        r.party_ledger_name || r.reference || "—",
        r.inward_qty ? `${r.inward_qty} ${data.item.unit}\n${formatINR(r.inward_value)}` : "—",
        r.outward_qty ? `${r.outward_qty} ${data.item.unit}\n${formatINR(r.outward_value)}` : "—",
        formatINR(r.rate),
        `${r.running_qty} ${data.item.unit}\n${formatINR(r.running_value)}`,
      ]);
    });
    rows.push({ cells: ["Closing Balance", "", "", "", "", "", "",
      `${data.closing.qty} ${data.item.unit}  (Rs. ${formatINR(data.closing.value)})`], type: "grand" });

    exportFinancialPDF(
      `item-ledger-${item?.name || iid}-${active?.name || "report"}`,
      `Item Ledger — ${item?.name || ""}`,
      [{ title: "", headers: head, rows }],
      {
        company: active?.name,
        address: active?.address,
        phone: active?.phone,
        email: active?.email,
        gstin: active?.gstin,
        pan: active?.pan,
        fy: formatFYLabel(applied),
        subtitle: `${formatRangePretty(applied)}  -  HSN ${data.item.hsn || "N/A"}  -  GST ${data.item.gst_rate}%  -  Unit ${data.item.unit}`,
        date: applied.end,
        place: active?.state || "",
        showSignatures: false,
        orientation: "landscape",
      },
    );
  };

  return (
    <>
      <PageHeader
        title={item ? `Item Ledger — ${item.name}` : "Item Ledger"}
        subtitle={`${active?.name || ""}  •  ${formatRangePretty(applied)}`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/reports/stock-summary")}
              className="text-xs text-blue-700 hover:underline flex items-center gap-1"
              data-testid="btn-back-to-summary"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Back to Stock Summary
            </button>
            <ExportToolbar onExcel={onCSV} onPDF={onPDF} onPrint={() => window.print()} />
          </div>
        }
      />
      <div className="p-6 space-y-4" data-testid="item-ledger-page">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-gray-600 font-medium">Item</label>
            <select
              value={iid}
              onChange={(e) => {
                setIid(e.target.value);
                navigate(`/reports/item-ledger/${e.target.value}`, { replace: true });
              }}
              className="h-8 rounded border border-gray-300 px-2 text-sm bg-white min-w-[260px]"
              data-testid="select-item-ledger"
            >
              <option value="">— Select an item —</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>{i.name}{i.hsn ? ` (${i.hsn})` : ""}</option>
              ))}
            </select>
          </div>
          <DateRangePicker
            start={range.start}
            end={range.end}
            onChange={setRange}
            onApply={apply}
            dirty={dirty}
          />
        </div>

        {!iid && (
          <div className="border border-dashed border-gray-300 rounded p-12 text-center bg-white">
            <Boxes className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Pick an item to view its date-wise ledger.</p>
          </div>
        )}

        {iid && !data && <div className="text-sm text-gray-500" data-testid="item-ledger-loading">Loading…</div>}

        {data && (
          <>
            {/* Item header card */}
            <div className="border border-gray-200 rounded bg-blue-50/30 p-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs" data-testid="item-meta-card">
              <Meta label="Item" value={data.item.name} />
              <Meta label="HSN/SAC" value={data.item.hsn || "—"} />
              <Meta label="Unit" value={data.item.unit} />
              <Meta label="GST Rate" value={`${data.item.gst_rate}%`} />
              <Meta label="Group" value={data.item.group} />
            </div>

            {/* Stat ribbon */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="item-ledger-totals">
              <Stat label="Opening" value={`${data.opening.qty} ${data.item.unit}`} sub={`Rs. ${formatINR(data.opening.value)}`} />
              <Stat label="Period Inward" value={`${data.totals.inward_qty} ${data.item.unit}`} sub={`Rs. ${formatINR(data.totals.inward_value)}`} tone="emerald" />
              <Stat label="Period Outward" value={`${data.totals.outward_qty} ${data.item.unit}`} sub={`Rs. ${formatINR(data.totals.outward_value)}`} tone="amber" />
              <Stat label="Closing" value={`${data.closing.qty} ${data.item.unit}`} sub={`Rs. ${formatINR(data.closing.value)}`} tone="blue" />
            </div>

            <div className="overflow-auto border border-gray-200 rounded bg-white">
              <table className="w-full text-xs" data-testid="item-ledger-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="text-left pl-3 py-2">Date</th>
                    <th className="text-left py-2">Voucher</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Party / Reference</th>
                    <th className="text-right py-2 bg-emerald-50/40" colSpan={2}>Inward (cost)</th>
                    <th className="text-right py-2 bg-amber-50/40" colSpan={2}>Outward ({(data.valuation_method || "FIFO").replaceAll("_", " ").toLowerCase()} cost)</th>
                    <th className="text-right py-2">Rate</th>
                    <th className="text-right py-2 bg-blue-50/40" colSpan={2}>Running Balance</th>
                  </tr>
                  <tr className="text-[9.5px] uppercase tracking-wider text-gray-400 border-t border-gray-100">
                    <th></th><th></th><th></th><th></th>
                    <th className="text-right py-1.5 bg-emerald-50/40">Qty</th>
                    <th className="text-right py-1.5 bg-emerald-50/40">Value (Rs.)</th>
                    <th className="text-right py-1.5 bg-amber-50/40">Qty</th>
                    <th className="text-right py-1.5 bg-amber-50/40">Value (Rs.)</th>
                    <th></th>
                    <th className="text-right py-1.5 bg-blue-50/40">Qty</th>
                    <th className="text-right py-1.5 bg-blue-50/40 pr-3">Value (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Opening row */}
                  <tr className="bg-gray-50 font-semibold border-b border-gray-200" data-testid="row-opening">
                    <td className="pl-3 py-2">{applied.start || "—"}</td>
                    <td colSpan={3} className="py-2 text-gray-600 italic">Opening Balance</td>
                    <td className="text-right tabular-nums bg-emerald-50/30">—</td>
                    <td className="text-right tabular-nums bg-emerald-50/30">—</td>
                    <td className="text-right tabular-nums bg-amber-50/30">—</td>
                    <td className="text-right tabular-nums bg-amber-50/30">—</td>
                    <td className="text-right tabular-nums">{data.opening.rate ? formatINR(data.opening.rate) : "—"}</td>
                    <td className="text-right tabular-nums bg-blue-50/30">{data.opening.qty} {data.item.unit}</td>
                    <td className="text-right tabular-nums bg-blue-50/30 pr-3">{formatINR(data.opening.value)}</td>
                  </tr>

                  {data.rows.length === 0 && (
                    <tr><td colSpan={11} className="text-center py-6 text-gray-400 italic" data-testid="empty-ledger">
                      No transactions for this item in the selected period.
                    </td></tr>
                  )}

                  {data.rows.map((r, idx) => (
                    <tr
                      key={`${r.voucher_id}-${idx}`}
                      className={`border-b border-gray-100 hover:bg-blue-50/30 ${r.is_oversell ? "bg-red-50/40" : ""}`}
                      data-testid={`row-${r.voucher_no}`}
                    >
                      <td className="pl-3 py-1.5 font-mono text-[10.5px]">{r.date}</td>
                      <td className="font-mono text-[10.5px] py-1.5">
                        <Link
                          to={`/vouchers/edit/${r.voucher_id}`}
                          state={{ from: location.pathname }}
                          className="text-blue-700 hover:underline"
                          title="Open voucher to edit. Saving will return you here with updated balances."
                          data-testid={`link-voucher-${r.voucher_no}`}
                        >
                          {r.voucher_no || "—"}
                        </Link>
                      </td>
                      <td className="py-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${VTYPE_TONE[r.voucher_type] || "bg-gray-100 text-gray-700"}`}>
                          {r.voucher_type}
                        </span>
                      </td>
                      <td className="py-1.5">
                        {r.party_ledger_name || <span className="text-gray-400 italic">{r.reference || "—"}</span>}
                        {r.is_oversell && (
                          <span
                            className="ml-2 text-[9.5px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase tracking-wider"
                            data-testid={`flag-oversell-${r.voucher_no}`}
                            title="Sale exceeded available stock — running balance went negative until the next purchase"
                          >
                            Oversold
                          </span>
                        )}
                      </td>
                      <td className="text-right tabular-nums bg-emerald-50/30">{r.inward_qty ? `${r.inward_qty} ${data.item.unit}` : "—"}</td>
                      <td className="text-right tabular-nums bg-emerald-50/30 text-emerald-700 font-medium">{r.inward_value ? formatINR(r.inward_value) : "—"}</td>
                      <td className="text-right tabular-nums bg-amber-50/30">{r.outward_qty ? `${r.outward_qty} ${data.item.unit}` : "—"}</td>
                      <td
                        className="text-right tabular-nums bg-amber-50/30 text-amber-700 font-medium"
                        title={r.sale_amount ? `Sales invoice value: Rs. ${formatINR(r.sale_amount)}` : ""}
                      >
                        {r.outward_value ? formatINR(r.outward_value) : "—"}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatINR(r.rate)}
                        {(r.discount_pct > 0 || r.discount_amount > 0) && (
                          <div className="text-[9px] text-emerald-700 leading-none">
                            −{r.discount_pct}% ({formatINR(r.discount_amount)})
                          </div>
                        )}
                      </td>
                      <td className={`text-right tabular-nums font-medium ${r.running_qty < 0 ? "bg-red-100 text-red-700" : "bg-blue-50/30"}`}>{r.running_qty} {data.item.unit}</td>
                      <td className={`text-right tabular-nums font-medium pr-3 ${r.running_qty < 0 ? "bg-red-100 text-red-700" : "bg-blue-50/30"}`}>{formatINR(r.running_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr className="font-semibold text-gray-900 text-[11px]" data-testid="row-closing">
                    <td className="pl-3 py-2">{applied.end || "—"}</td>
                    <td colSpan={3} className="py-2 italic text-gray-700">Closing Balance</td>
                    <td className="text-right tabular-nums bg-emerald-50/40">{data.totals.inward_qty} {data.item.unit}</td>
                    <td className="text-right tabular-nums bg-emerald-50/40 text-emerald-800">{formatINR(data.totals.inward_value)}</td>
                    <td className="text-right tabular-nums bg-amber-50/40">{data.totals.outward_qty} {data.item.unit}</td>
                    <td className="text-right tabular-nums bg-amber-50/40 text-amber-800">{formatINR(data.totals.outward_value)}</td>
                    <td className="text-right tabular-nums">{data.closing.rate ? formatINR(data.closing.rate) : "—"}</td>
                    <td className="text-right tabular-nums bg-blue-50/40">{data.closing.qty} {data.item.unit}</td>
                    <td className="text-right tabular-nums bg-blue-50/40 pr-3">{formatINR(data.closing.value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const VTYPE_TONE = {
  "Purchase": "bg-emerald-100 text-emerald-800",
  "Sales": "bg-amber-100 text-amber-800",
  "Credit Note": "bg-blue-100 text-blue-800",
  "Debit Note": "bg-red-100 text-red-800",
};

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{label}</div>
      <div className="font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    blue: "border-blue-200 bg-blue-50/40",
  };
  return (
    <div className={`border rounded px-3 py-2 ${tones[tone] || "border-gray-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{label}</div>
      <div className="text-base font-semibold text-gray-900 mt-0.5 font-mono tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-600 font-mono tabular-nums">{sub}</div>}
    </div>
  );
}

export default function ItemLedger() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
